/* =========================================================
   The Circle — backend bridge.

   Everything Circle-related that talks to the VRO app goes through here:
   the App Proxy client, the login popup, and the DPDPA consent prompt.
   Loaded before the-circle.js / circle-take.js, which use it via
   window.VroCircleApi.

   The app is reached through Shopify's App Proxy (/apps/vro/circle/...),
   never the backend's own domain — the proxy is what signs the request
   and tells the backend who the logged-in customer is. Calling the
   backend directly would arrive unsigned and unauthenticated.
   ========================================================= */
(function () {
  'use strict';

  var API = '/apps/vro/circle';
  var CONSENT_VERSION = 'v1';

  /* ---------------------------------------------------------------
     Fetch wrapper. Errors carry .status and .code so callers can act
     on the specific case (login_required, consent_required, ...)
     rather than string-matching a message.
     --------------------------------------------------------------- */
  function api(path, options) {
    options = options || {};
    return fetch(API + path, {
      method: options.method || 'GET',
      headers: Object.assign(
        { 'Content-Type': 'application/json', Accept: 'application/json' },
        options.headers || {}
      ),
      body: options.body ? JSON.stringify(options.body) : undefined
    }).then(function (res) {
      return res.text().then(function (text) {
        var data = null;
        try { data = text ? JSON.parse(text) : null; } catch (e) { /* empty/non-JSON body */ }
        if (res.ok) return data;

        var err = new Error((data && data.error) || 'request_failed');
        err.status = res.status;
        err.code = data && data.error;
        err.data = data;
        throw err;
      });
    });
  }

  /* ---------------------------------------------------------------
     Login — the theme's own sheet, not one of ours.

     Circle needs a real Shopify session (that's what the App Proxy
     signs its requests with), and the theme's login sheet already
     offers exactly that: "Sign in with your Shopify account instead",
     alongside its phone OTP. So Circle just opens that sheet and gets
     out of the way — one login on this store, the one the customer
     already knows, and nothing here to keep in step with it.

     Shopify's hosted login is a full page navigation, so whatever the
     customer was doing when it opened is gone by the time they're back.
     That's the trade for not running a second login of our own; they
     land signed in and tap the thing again.
     --------------------------------------------------------------- */
  /* The store's real login route, handed down from Liquid
     (routes.account_login_url) — classic accounts and new customer
     accounts live at different paths, and guessing wrong opens the popup
     on a 404 where the login can never complete.

     Both return params are sent because the two systems read different
     ones (classic: return_url, new: return_to); the one that doesn't
     apply is ignored. */
  function loginUrl(returnPath) {
    var base = window.__vroLoginUrl || '/customer_authentication/login';
    var back = encodeURIComponent(returnPath || (window.location.pathname + window.location.search));
    return base + (base.indexOf('?') === -1 ? '?' : '&') +
      'return_to=' + back + '&return_url=' + back;
  }

  function isLoggedIn() {
    return api('/me').then(function () {
      loggedInHint = true;
      return true;
    }).catch(function (err) {
      if (err.status === 401) return false;
      throw err;
    });
  }

  /* What Liquid knew when the page rendered, kept up to date by any
     login that happens after. Screens use it to skip calls that would
     only come back 401 — a guest opening the cart shouldn't fire a
     request per panel just to be told they're a guest.

     Only ever a shortcut for the negative case. Once someone logs in
     this flips true and the real request decides everything after. */
  var loggedInHint = (window.__vroLoggedIn === true);
  function isGuest() { return !loggedInHint; }

  /* Deliberately does NOT touch the profile drawer or anything else the
     theme gates on its own session.

     Circle's login and the theme's login are two different things: this
     one creates a Shopify session (the only kind the app can verify),
     the theme's bottom sheet creates its own Firebase-backed one. An
     earlier version of this revealed the profile's `.profile-gated`
     sections after a Shopify login, which showed that drawer half-filled
     — its own script populates it from the theme's session, which a
     Shopify login doesn't produce. The drawer is left exactly as the
     theme built it; bridging the two sessions is its own piece of work.

     The event is still dispatched, since it means "the customer signed
     in" regardless of which door they used, and anything listening can
     decide for itself what to do about it. */
  function onLoggedIn() {
    loggedInHint = true;
    document.dispatchEvent(new CustomEvent('vro:session-changed'));
  }

  /* Whatever Circle has on screen gets out of the way before the login
     sheet opens. Two reasons, both seen in practice: the sheet sits
     lower in the stack than Circle's modals, so it opened *behind* the
     create-list dialog; and even stacked correctly, a half-filled form
     the customer is about to lose is not what should be underneath a
     login. They come back signed in and start it again, cleanly. */
  function closeCircleUi() {
    var open = document.querySelectorAll(
      '.circle-modal.is-open, .circle-modal-backdrop.is-visible'
    );
    Array.prototype.forEach.call(open, function (el) {
      el.classList.remove('is-open');
      el.classList.remove('is-visible');
      if (el.classList.contains('circle-modal')) {
        el.setAttribute('inert', '');
        el.setAttribute('aria-hidden', 'true');
      } else {
        setTimeout(function () { el.hidden = true; }, 320);
      }
    });
    // The sheet manages the scroll lock itself from here.
    document.body.style.overflow = '';
  }

  /**
   * Opens the theme's login sheet. Returns true if it opened, false if
   * this page has no sheet to open (then it goes straight to Shopify's
   * login instead, rather than leaving the tap doing nothing).
   *
   * Nothing waits on the result: signing in navigates away, and the page
   * comes back already signed in.
   */
  function login() {
    closeCircleUi();

    /* Asks the sheet directly rather than clicking [data-login-open].
       That trigger opens the profile popup instead for anyone who
       already has a theme (phone OTP) session — which is exactly the
       customer who still needs a Shopify one, so they'd be handed the
       one screen that can't give it to them. */
    if (window.VroLoginSheet && typeof window.VroLoginSheet.open === 'function') {
      window.VroLoginSheet.open();
      return true;
    }

    var trigger = document.querySelector('[data-login-open]');
    if (trigger) {
      trigger.click();
      return true;
    }
    window.location.href = loginUrl();
    return false;
  }

  /**
   * Gate for anything that only makes sense signed in. Returns true to
   * carry on; returns false having opened the login sheet.
   *
   * Screens call this *before* opening their own UI, so a guest meets
   * the login first instead of filling in a form that is only going to
   * be refused. The request-level 401 handling in call() stays as the
   * backstop for the session that expired mid-flow.
   */
  function requireLogin() {
    if (!isGuest()) return true;
    login();
    return false;
  }

  /* ---------------------------------------------------------------
     Consent (DPDPA).

     Creating a list and voting both start personal data flowing to
     other people, so the backend refuses them with 403 consent_required
     until consent is on record. Asked once — after that the backend
     remembers and this never fires again.

     Built from the theme's own .circle-modal markup so it looks like
     the rest of the flow rather than a browser dialog.
     --------------------------------------------------------------- */
  var consentEls = null;

  function buildConsentModal() {
    if (consentEls) return consentEls;

    // --consent lifts both above the "Your take" overlay (z-index 3100).
    // Without it the prompt opens BEHIND that popup: the friend sees
    // nothing happen, can never agree, and every vote keeps coming back
    // 403 consent_required.
    var backdrop = document.createElement('div');
    backdrop.className = 'circle-modal-backdrop circle-modal-backdrop--consent';
    backdrop.hidden = true;

    var modal = document.createElement('div');
    modal.className = 'circle-modal circle-modal--consent';
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('inert', '');
    modal.innerHTML =
      '<div class="circle-modal-head">' +
        '<h2 class="circle-modal-title">Before you share</h2>' +
      '</div>' +
      '<p class="circle-modal-consent-copy">' +
        'Circle shares the items, votes and notes you add with the people you ' +
        'choose to send them to, so they can see them too. This is stored so ' +
        'you and your Circle can see it later.' +
      '</p>' +
      '<div class="circle-modal-actions">' +
        '<button type="button" class="circle-modal-cancel" data-vro-consent-cancel>Cancel</button>' +
        '<button type="button" class="circle-modal-create-btn" data-vro-consent-agree>Agree &amp; continue</button>' +
      '</div>';

    document.body.appendChild(backdrop);
    document.body.appendChild(modal);

    consentEls = { backdrop: backdrop, modal: modal };
    return consentEls;
  }

  var pendingConsent = null;   // { resolve, reject }

  function closeConsent(decline) {
    if (!consentEls) return;
    consentEls.backdrop.classList.remove('is-visible');
    consentEls.modal.classList.remove('is-open');
    consentEls.modal.setAttribute('inert', '');
    consentEls.modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    setTimeout(function () { consentEls.backdrop.hidden = true; }, 320);

    if (decline && pendingConsent) {
      var p = pendingConsent;
      pendingConsent = null;
      var err = new Error('consent_declined');
      err.code = 'consent_declined';
      p.reject(err);
    }
  }

  function askConsent() {
    var els = buildConsentModal();
    return new Promise(function (resolve, reject) {
      pendingConsent = { resolve: resolve, reject: reject };
      els.backdrop.hidden = false;
      requestAnimationFrame(function () {
        els.backdrop.classList.add('is-visible');
        els.modal.classList.add('is-open');
      });
      els.modal.removeAttribute('inert');
      els.modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    });
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-vro-consent-agree]')) {
      var p = pendingConsent;
      pendingConsent = null;
      closeConsent(false);
      if (p) p.resolve({ granted: true, notice_version: CONSENT_VERSION });
      return;
    }
    if (e.target.closest('[data-vro-consent-cancel]') || (consentEls && e.target === consentEls.backdrop)) {
      closeConsent(true);
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && consentEls && consentEls.modal.classList.contains('is-open')) {
      closeConsent(true);
    }
  });

  /* ---------------------------------------------------------------
     call() — the one callers should normally use. Runs `fn(extra)`,
     and handles the two things the backend can demand mid-request:
     a login, and a consent record. `extra` is merged into the request
     body by the caller, so the retry carries the consent through.
     --------------------------------------------------------------- */
  function call(fn) {
    return fn({}).catch(function (err) {
      if (err.status === 401 && err.code === 'login_required') {
        // Opens the theme's sheet and stops here. Signing in is a page
        // navigation, so there's nothing to resume — the customer comes
        // back signed in and taps again.
        login();
        throw err;
      }
      if (err.status === 403 && err.code === 'consent_required') {
        return askConsent().then(function (consent) {
          return fn({ consent: consent });
        });
      }
      throw err;
    });
  }

  /* ---------------------------------------------------------------
     Pending state for a tapped button.

     Every Circle action goes over the App Proxy, which on a phone is
     often a visible wait — and until now nothing on screen said so, so
     the tap read as having missed. This marks the button instead:
     `disabled` so a second tap can't fire the same request, and
     .circle-busy so it shows a spinner where its label was.

     Lives here rather than in the-circle.js because circle-take.js and
     the profile section need the same thing and this file is the one
     they all already depend on.
     --------------------------------------------------------------- */
  function busy(btn, on) {
    if (!btn) return;
    btn.disabled = !!on;
    btn.classList.toggle('circle-busy', !!on);
  }

  window.VroCircleApi = {
    api: api,
    call: call,
    busy: busy,
    login: login,
    requireLogin: requireLogin,
    isLoggedIn: isLoggedIn,
    isGuest: isGuest,
    loginUrl: loginUrl,
    CONSENT_VERSION: CONSENT_VERSION
  };
})();
